import React, { Fragment } from "react"
import PropTypes from "prop-types"
import { MdEdit, MdHelpOutline } from "react-icons/md"
import { Button, Heading, LinkButton, Text } from "gatsby-interface"
import { hideOnMobileCss } from "@modules/ui/utils/responsive"

const settingsCardCss = theme => [
  theme.cardStyles.frame,
  {
    display: `grid`,
    gridGap: theme.space[5],
    gridTemplateColumns: `1fr auto`,
    gridTemplateRows: `auto auto`,

    padding: theme.space[7],

    [theme.mediaQueries.desktop]: theme.cardStyles.space.L,
  },
]

export function SettingsCard({ children, variant = `DEFAULT`, ...rest }) {
  return (
    <section
      css={theme =>
        variant === `PLAIN` ? theme.cardStyles.frame : settingsCardCss(theme)
      }
      {...rest}
    >
      {children}
    </section>
  )
}

SettingsCard.propTypes = {
  children: PropTypes.any,
}

const settingsCardTitleCss = theme => ({
  alignItems: `center`,
  display: `flex`,
  fontSize: theme.fontSizes[3],
  lineHeight: 1,
  minHeight: `2.25rem`,
})

export function SettingsCardTitle({ children, titleAs = "h3", ...rest }) {
  return (
    <Heading as={titleAs} css={settingsCardTitleCss} {...rest}>
      {children}
    </Heading>
  )
}

const descriptionCss = theme => ({
  color: theme.colors.grey[50],
  fontSize: theme.fontSizes[1],
  fontFamily: theme.fonts.body,
  lineHeight: 1.4,
  margin: 0,

  "&:not(:last-child)": {
    marginBottom: theme.space[5],
  },
})

const noteCss = _theme => ({
  marginLeft: `auto`,
})

export function SettingsCardDescription({ children, ...rest }) {
  return (
    <p css={descriptionCss} {...rest}>
      {children}
    </p>
  )
}

export function SettingsCardEditButton({
  children = undefined,
  label = `Edit`,
  showIcon = true,
  ...rest
}) {
  return (
    <Button
      variant={`GHOST`}
      {...rest}
      css={theme => ({ marginRight: `-${theme.space[5]}` })}
    >
      {children || (
        <Fragment>
          <span aria-hidden="true" css={hideOnMobileCss}>
            {label}
          </span>

          {showIcon && <MdEdit aria-label={label} />}
        </Fragment>
      )}
    </Button>
  )
}

export function SettingsCardContent({ children, ...props }) {
  return (
    <div css={{ gridColumn: `1 / 3`, maxWidth: "100%" }} {...props}>
      {children}
    </div>
  )
}

export function SettingsCardDoclink({ ...rest }) {
  return (
    <LinkButton
      variant={`GHOST`}
      tone={`NEUTRAL`}
      css={theme => ({
        marginLeft: theme.space[2],
      })}
      {...rest}
    >
      <MdHelpOutline />
    </LinkButton>
  )
}

SettingsCardDoclink.propTypes = {
  "aria-label": PropTypes.string.isRequired,
}

const blockDescriptionCss = theme => ({
  "&:first-of-type": {
    marginTop: theme.space[8],
  },

  [theme.mediaQueries.desktop]: {
    gridColumn: `1 / 2`,
  },
})

export function SettingsBlock({
  children,
  title,
  note = ``,
  description = undefined,
  docUrl,
  ...rest
}) {
  return (
    <SettingsCard
      css={theme => ({
        display: `block`,
        padding: 0,
        [theme.mediaQueries.desktop]: { padding: 0 },
      })}
      {...rest}
    >
      {title && (
        <SettingsBlockHeader>
          {title && (
            <SettingsCardTitle
              css={theme => ({ marginBottom: `-${theme.space[3]}` })}
            >
              {title}{" "}
              {docUrl && (
                <SettingsCardDoclink
                  to={docUrl}
                  aria-label={`Documentation for ${title}`}
                />
              )}
              {note && (
                <Text
                  css={noteCss}
                  noMarginBottom={true}
                  noMarginTop={true}
                  size="S"
                >
                  {note}
                </Text>
              )}
            </SettingsCardTitle>
          )}
          {description && (
            <SettingsCardDescription css={blockDescriptionCss}>
              {description}
            </SettingsCardDescription>
          )}
        </SettingsBlockHeader>
      )}
      {children}
    </SettingsCard>
  )
}

SettingsBlock.propTypes = {
  children: PropTypes.any,
  title: PropTypes.node,
  description: PropTypes.node,
  docUrl: PropTypes.string,
}

export function SettingsBlockHeader({ children, ...rest }) {
  return (
    <header
      css={theme => [
        {
          fontSize: theme.fontSizes[4],
          padding: theme.space[7],

          [theme.mediaQueries.desktop]: theme.cardStyles.space.L,
        },
      ]}
      {...rest}
    >
      {children}
    </header>
  )
}

export function SettingsBlockContent({ children, ...rest }) {
  return (
    <div
      css={{
        display: `flex`,
        flexDirection: `column`,
      }}
      {...rest}
    >
      {children}
    </div>
  )
}

export function SettingsCardSection({
  children,
  applyPaddingTop = true,
  applyPaddingBottom = true,
  applyPaddingLeft = true,
  applyPaddingRight = true,
}) {
  return (
    <div
      css={theme => [
        applyPaddingTop && {
          paddingTop: theme.space[7],
          [theme.mediaQueries.desktop]: {
            paddingTop: theme.space[7],
          },
        },
        applyPaddingBottom && {
          paddingBottom: theme.space[7],
          [theme.mediaQueries.desktop]: {
            paddingBottom: theme.space[8],
          },
        },
        applyPaddingLeft && {
          paddingLeft: theme.space[7],
          [theme.mediaQueries.desktop]: {
            paddingLeft: theme.space[9],
          },
        },
        applyPaddingRight && {
          paddingRight: theme.space[7],
          [theme.mediaQueries.desktop]: {
            paddingRight: theme.space[9],
          },
        },
      ]}
    >
      {children}
    </div>
  )
}

const footerCss = theme => ({
  background: theme.colors.purple[5],
  paddingTop: theme.space[7],
  paddingBottom: theme.space[7],
  paddingLeft: theme.space[9],
  paddingRight: theme.space[9],
  display: `flex`,
  alignItems: `center`,
  justifyContent: `space-between`,
})

export function SettingsCardFooter({ children, variant = `DEFAULT` }) {
  return (
    <div
      css={theme => [
        footerCss(theme),
        variant === `IN_SECTION` && {
          marginLeft: `-${theme.space[7]}`,
          marginRight: `-${theme.space[7]}`,
          marginBottom: `-${theme.space[7]}`,

          [theme.mediaQueries.desktop]: {
            marginLeft: `-${theme.space[9]}`,
            marginRight: `-${theme.space[9]}`,
            marginBottom: `-${theme.space[8]}`,
          },
        },
      ]}
    >
      {children}
    </div>
  )
}
